import { ArrowLeft, Bike, CheckCircle, Download, FileText, ShieldAlert } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { PictoConformite } from '@/assets/icons-april-fr/pictos';
import APILink from '@/components/shared/api-link';
import AprilPicto from '@/components/shared/april-picto';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useFleetVehicle } from '@/hooks/api/use-fleet';
import { isReductionAllianz } from '@/lib/contracts';
import { formatDate as formatDateFr, isValidDate } from '@/lib/dates';
import type { FleetVehicle } from '@/types/api/fleet';
import { formatImmatriculation } from '@/lib/helpers';

const formatDate = (value?: string) => {
    if (!value) return null;
    return isValidDate(value) ? formatDateFr(value) : value;
};

const isLeasing = (v: unknown) => v === 'T' || v === true;

export default function FlotteDetail() {
    const { id, vehiculeId } = useParams();
    const vehiculeQuery = useFleetVehicle(id ?? '', vehiculeId ?? '');
    const vehicule: FleetVehicle = vehiculeQuery.data ?? {};

    const garantiesFiltrees = (vehicule.garanties || []).filter(
        g => !['FRAIS_GESTION', 'FRAIS_FRAC', 'FRAIS_VARIATION'].includes(g.code_famille ?? '') && !isReductionAllianz(g)
    );

    const titleVehicule = `${vehicule.marque || ''} ${vehicule.modele || ''}`.trim() || 'Véhicule';

    if (vehiculeQuery.isPending) return <SmallSpinner />;

    return (
        <div className="space-y-6 mt-4">
            {/* Header navigation */}
            <div className="flex items-center justify-between gap-3">
                <Button asChild variant="outline" size="sm">
                    <Link to={`/mes-contrats/mon-contrat/${vehicule.contrat}`}>
                        <ArrowLeft className="w-4 h-4" />
                        Retour au contrat
                    </Link>
                </Button>
            </div>

            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-6 text-white">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-xs sm:text-sm opacity-90 mb-1">Adhésion N°{vehicule.adhesion}</p>
                    <h1 className="text-xl sm:text-3xl font-semibold mb-6 flex items-center gap-3">
                        <Bike className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
                        {titleVehicule}
                    </h1>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))] gap-3 sm:gap-6">
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Immatriculation</p>
                            <p className="text-sm sm:text-lg font-semibold font-mono uppercase">
                                {vehicule.immatriculation ? formatImmatriculation(vehicule.immatriculation) : '—'}
                            </p>
                        </div>
                        {vehicule.cylindree && (
                            <div>
                                <p className="text-xs sm:text-sm opacity-80 mb-0.5">Cylindrée</p>
                                <p className="text-sm sm:text-lg font-semibold">{vehicule.cylindree}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Mise en circulation</p>
                            <p className="text-sm sm:text-lg font-semibold">{formatDate(vehicule.dateMEC) || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Statut</p>
                            <Badge variant={vehicule.inactif ? 'secondary' : 'success'} className="text-xs sm:text-sm">
                                {vehicule.inactif ? 'Inactif' : 'Actif'}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions Cards */}
            {(vehicule.memo_link || !vehicule.inactif) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {vehicule.memo_link && (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary flex flex-col">
                            <CardContent className="pt-6 text-center flex flex-col flex-1">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                    <FileText className="w-6 h-6 text-primary" />
                                </div>
                                <p className="text-sm font-semibold mb-1">Mémo véhicule</p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Toutes les infos d'adhésion de votre véhicule en un PDF.
                                </p>
                                <div className="mt-auto">
                                    <APILink
                                        href={vehicule.memo_link}
                                        className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md border-2 border-primary text-primary hover:border-foreground hover:text-foreground transition-all duration-300 ease-in-out cursor-pointer no-underline"
                                    >
                                        <Download className="w-4 h-4" />
                                        Télécharger le mémo
                                    </APILink>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {!vehicule.inactif && (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-destructive flex flex-col">
                            <CardContent className="pt-6 text-center flex flex-col flex-1">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10 mx-auto mb-4">
                                    <ShieldAlert className="w-6 h-6 text-destructive" />
                                </div>
                                <p className="text-sm font-semibold mb-1">Déclarer un sinistre</p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Vous avez eu un accident, un vol ou un incident avec ce véhicule ?
                                </p>
                                <div className="mt-auto">
                                    <Button asChild variant="destructive" size="sm">
                                        <Link to={`/mes-sinistres/je-declare-un-sinistre/vehicule/${vehiculeId}`}>
                                            Déclarer un sinistre
                                        </Link>
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Informations véhicule */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Bike className="w-5 h-5 text-primary" />
                        Informations du véhicule
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Adhésion</p>
                            <p className="font-medium">{vehicule.adhesion ?? '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Véhicule</p>
                            <p className="font-medium">{titleVehicule}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Immatriculation</p>
                            <p className="font-medium font-mono uppercase">
                                {vehicule.immatriculation ? formatImmatriculation(vehicule.immatriculation) : '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Cylindrée</p>
                            <p className="font-medium">{vehicule.cylindree || '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Mise en circulation</p>
                            <p className="font-medium">{formatDate(vehicule.dateMEC) || '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Date d'entrée</p>
                            <p className="font-medium">{formatDate(vehicule.dateEntree) || '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Date de sortie</p>
                            <p className="font-medium">{formatDate(vehicule.dateSortie) || '—'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Financement en leasing</p>
                            <p className="font-medium">{isLeasing(vehicule.leasing) ? 'Oui' : 'Non'}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Garanties */}
            {garantiesFiltrees.length > 0 && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AprilPicto icon={PictoConformite} size="md" />
                            Garanties et options
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid gap-3">
                            {garantiesFiltrees.map((g, i) => (
                                <div
                                    key={`${g.code_famille}-${i}`}
                                    className="flex items-center p-4 bg-muted/50 rounded-lg transition-colors duration-200 hover:bg-primary/10"
                                >
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success text-white shrink-0 mr-3">
                                        <CheckCircle className="w-4 h-4" />
                                    </div>
                                    <p className="font-medium">{g.libelle}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
